import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Navbar } from "@/components/Navbar";
import { 
  Mail, 
  Users, 
  Play, 
  Pause, 
  Plus, 
  Trash2, 
  Settings,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  Sparkles,
  ArrowLeft,
  RefreshCw,
  Upload
} from "lucide-react";
import { adminFetch } from "@/lib/adminAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stats Card Component
const StatCard = ({ label, value, icon: Icon, color = "blue" }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    yellow: "bg-yellow-50 text-yellow-600",
    red: "bg-red-50 text-red-600",
    purple: "bg-purple-50 text-purple-600"
  };
  
  return (
    <div className="text-center p-4">
      <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center mx-auto mb-2`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  );
};

// Customer Row Component
const CustomerRow = ({ customer, onDelete }) => {
  const statusColors = {
    pending: "bg-yellow-100 text-yellow-700",
    sent: "bg-blue-100 text-blue-700",
    delivered: "bg-green-100 text-green-700",
    opened: "bg-purple-100 text-purple-700",
    clicked: "bg-indigo-100 text-indigo-700",
    reviewed: "bg-emerald-100 text-emerald-700",
    failed: "bg-red-100 text-red-700"
  };
  
  return (
    <div className="flex items-center justify-between p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50">
      <div className="flex-1">
        <div className="font-medium text-slate-900">{customer.name}</div>
        <div className="text-sm text-slate-500">{customer.email}</div>
      </div>
      <div className="flex items-center gap-3">
        {customer.service_date && (
          <span className="text-xs text-slate-400">
            Service: {new Date(customer.service_date).toLocaleDateString()}
          </span>
        )}
        <Badge className={statusColors[customer.status] || "bg-slate-100"}>
          {customer.status}
        </Badge>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => onDelete(customer.id)}
          className="text-red-500 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Add Customer Form
const AddCustomerForm = ({ campaignId, onAdded }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [serviceDate, setServiceDate] = useState("");
  const [serviceDesc, setServiceDesc] = useState("");
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!name || !email) return;
    setAdding(true);
    
    try {
      const response = await adminFetch(`${API_URL}/api/campaigns/customers/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          campaign_id: campaignId,
          name,
          email,
          service_date: serviceDate || null,
          service_description: serviceDesc || null
        })
      });
      
      if (response.ok) {
        setName("");
        setEmail("");
        setServiceDate("");
        setServiceDesc("");
        onAdded();
      }
    } catch (error) {
      console.error("Failed to add customer:", error);
    }
    setAdding(false);
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Plus className="w-5 h-5 text-blue-600" />
          Add Customer
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Customer Name</Label>
            <Input 
              placeholder="John Smith" 
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="customer-name-input"
            />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input 
              type="email"
              placeholder="john@example.com" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="customer-email-input"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Service Date (Optional)</Label>
            <Input 
              type="date"
              value={serviceDate}
              onChange={(e) => setServiceDate(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Service Description (Optional)</Label>
            <Input 
              placeholder="e.g., Oil change" 
              value={serviceDesc}
              onChange={(e) => setServiceDesc(e.target.value)}
            />
          </div>
        </div>
        <Button 
          onClick={handleAdd}
          disabled={adding || !name || !email}
          className="w-full bg-blue-600 hover:bg-blue-700"
          data-testid="add-customer-btn"
        >
          {adding ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
          Add to Campaign
        </Button>
      </CardContent>
    </Card>
  );
};

// Campaign Settings Panel
const CampaignSettings = ({ campaign, onUpdate }) => {
  const [delayHours, setDelayHours] = useState(campaign?.delay_hours || 24);
  const [sendHour, setSendHour] = useState(campaign?.send_time_hour || 10);
  const [useAI, setUseAI] = useState(campaign?.use_ai_content !== false);
  const [maxPerDay, setMaxPerDay] = useState(campaign?.max_emails_per_day || 50);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminFetch(`${API_URL}/api/campaigns/${campaign.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          delay_hours: delayHours,
          send_time_hour: sendHour,
          use_ai_content: useAI,
          max_emails_per_day: maxPerDay
        })
      });
      onUpdate();
    } catch (error) {
      console.error("Failed to save settings:", error);
    }
    setSaving(false);
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Settings className="w-5 h-5 text-slate-600" />
          Campaign Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-sm">Delay After Service (hours)</Label>
          <Input 
            type="number"
            min={1}
            max={168}
            value={delayHours}
            onChange={(e) => setDelayHours(parseInt(e.target.value))}
          />
          <p className="text-xs text-slate-500 mt-1">
            Wait {delayHours} hours after service date before sending invite
          </p>
        </div>
        
        <div>
          <Label className="text-sm">Preferred Send Time</Label>
          <select 
            className="w-full border rounded-md p-2 text-sm"
            value={sendHour}
            onChange={(e) => setSendHour(parseInt(e.target.value))}
          >
            {[...Array(24)].map((_, i) => (
              <option key={i} value={i}>
                {i === 0 ? "12:00 AM" : i < 12 ? `${i}:00 AM` : i === 12 ? "12:00 PM" : `${i-12}:00 PM`}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <Label className="text-sm">Max Emails Per Day</Label>
          <Input 
            type="number"
            min={1}
            max={500}
            value={maxPerDay}
            onChange={(e) => setMaxPerDay(parseInt(e.target.value))}
          />
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm">Use AI-Generated Content</Label>
            <p className="text-xs text-slate-500">Personalize each email with AI</p>
          </div>
          <Switch checked={useAI} onCheckedChange={setUseAI} />
        </div>
        
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="w-full"
          variant="outline"
        >
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </CardContent>
    </Card>
  );
};

// Main Campaign Detail Page
export const CampaignDetail = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchCampaign();
    fetchCustomers();
  }, [campaignId]);

  const fetchCampaign = async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/campaigns/${campaignId}`);
      if (response.ok) {
        setCampaign(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch campaign:", error);
    }
    setLoading(false);
  };

  const fetchCustomers = async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/campaigns/${campaignId}/customers?limit=100`);
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
      }
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const toggleCampaignStatus = async () => {
    const action = campaign.status === "active" ? "pause" : "activate";
    try {
      const response = await adminFetch(`${API_URL}/api/campaigns/${campaignId}/${action}`, {
        method: "POST"
      });
      if (response.ok) {
        fetchCampaign();
      }
    } catch (error) {
      console.error("Failed to toggle campaign:", error);
    }
  };

  const processInvites = async () => {
    setProcessing(true);
    try {
      const response = await adminFetch(`${API_URL}/api/campaigns/${campaignId}/process?max_to_send=10`, {
        method: "POST"
      });
      if (response.ok) {
        const result = await response.json();
        alert(`Processed: ${result.sent} sent, ${result.failed} failed`);
        fetchCampaign();
        fetchCustomers();
      }
    } catch (error) {
      console.error("Failed to process invites:", error);
    }
    setProcessing(false);
  };

  const deleteCustomer = async (customerId) => {
    try {
      await adminFetch(`${API_URL}/api/campaigns/customers/${customerId}`, {
        method: "DELETE"
      });
      fetchCustomers();
      fetchCampaign();
    } catch (error) {
      console.error("Failed to delete customer:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-20 max-w-4xl mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Campaign Not Found</h1>
          <Button onClick={() => navigate("/campaigns")}>View All Campaigns</Button>
        </div>
      </div>
    );
  }

  const stats = campaign.stats || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="campaign-detail">
      <Navbar />
      
      <div className="pt-20 pb-12">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="mb-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/campaigns")}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              All Campaigns
            </Button>
            
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">{campaign.name}</h1>
                <p className="text-slate-600">
                  {campaign.use_ai_content ? "AI-powered" : "Standard"} review request campaign
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge 
                  className={campaign.status === "active" 
                    ? "bg-green-100 text-green-700" 
                    : campaign.status === "paused" 
                    ? "bg-yellow-100 text-yellow-700"
                    : "bg-slate-100 text-slate-700"
                  }
                >
                  {campaign.status}
                </Badge>
                <Button
                  onClick={toggleCampaignStatus}
                  variant={campaign.status === "active" ? "outline" : "default"}
                  className={campaign.status !== "active" ? "bg-green-600 hover:bg-green-700" : ""}
                  data-testid="toggle-campaign-btn"
                >
                  {campaign.status === "active" ? (
                    <>
                      <Pause className="w-4 h-4 mr-2" />
                      Pause
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Activate
                    </>
                  )}
                </Button>
                <Button
                  onClick={processInvites}
                  disabled={processing || campaign.status !== "active"}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="process-invites-btn"
                >
                  {processing ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Pending
                </Button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <Card className="border-slate-200 mb-6">
            <CardContent className="p-0">
              <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-slate-100">
                <StatCard label="Total Customers" value={stats.total_customers || 0} icon={Users} color="blue" />
                <StatCard label="Emails Sent" value={stats.sent || 0} icon={Send} color="purple" />
                <StatCard label="Opened" value={`${stats.open_rate || 0}%`} icon={Mail} color="green" />
                <StatCard label="Clicked" value={`${stats.click_rate || 0}%`} icon={TrendingUp} color="yellow" />
                <StatCard label="Reviews" value={stats.reviewed || 0} icon={CheckCircle} color="green" />
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Customer List */}
            <div className="lg:col-span-2">
              <Card className="border-slate-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      Customers ({customers.length})
                    </CardTitle>
                    <div className="flex gap-2">
                      <Badge variant="outline">{stats.pending || 0} pending</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0 max-h-96 overflow-y-auto">
                  {customers.length > 0 ? (
                    customers.map((customer) => (
                      <CustomerRow 
                        key={customer.id} 
                        customer={customer} 
                        onDelete={deleteCustomer}
                      />
                    ))
                  ) : (
                    <div className="p-8 text-center text-slate-500">
                      <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p>No customers in this campaign yet.</p>
                      <p className="text-sm">Add customers to start sending review invites.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <AddCustomerForm 
                campaignId={campaignId} 
                onAdded={() => { fetchCustomers(); fetchCampaign(); }}
              />
              <CampaignSettings 
                campaign={campaign} 
                onUpdate={fetchCampaign}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Campaign List Page
export const CampaignsList = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  
  // Create form state
  const [newName, setNewName] = useState("");
  const [selectedBusiness, setSelectedBusiness] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchCampaigns();
    fetchBusinesses();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/campaigns`);
      if (response.ok) {
        setCampaigns(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch campaigns:", error);
    }
    setLoading(false);
  };

  const fetchBusinesses = async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/businesses`);
      if (response.ok) {
        setBusinesses(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch businesses:", error);
    }
  };

  const createCampaign = async () => {
    if (!newName || !selectedBusiness) return;
    setCreating(true);
    
    try {
      const response = await adminFetch(`${API_URL}/api/campaigns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          business_id: selectedBusiness,
          name: newName,
          trigger_type: "manual",
          delay_hours: 24,
          use_ai_content: true
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        navigate(`/campaigns/${data.campaign.id}`);
      }
    } catch (error) {
      console.error("Failed to create campaign:", error);
    }
    setCreating(false);
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm("Are you sure you want to delete this campaign?")) return;
    
    try {
      await adminFetch(`${API_URL}/api/campaigns/${campaignId}`, {
        method: "DELETE"
      });
      fetchCampaigns();
    } catch (error) {
      console.error("Failed to delete campaign:", error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="campaigns-list">
      <Navbar />
      
      <div className="pt-20 pb-12">
        <div className="max-w-5xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Review Campaigns</h1>
              <p className="text-slate-600">Automate review request emails to your customers</p>
            </div>
            <Button 
              onClick={() => setShowCreate(!showCreate)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="create-campaign-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Campaign
            </Button>
          </div>

          {/* Create Form */}
          {showCreate && (
            <Card className="border-blue-200 bg-blue-50/50 mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Create New Campaign</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Campaign Name</Label>
                    <Input 
                      placeholder="e.g., Post-Service Review Request"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      data-testid="new-campaign-name"
                    />
                  </div>
                  <div>
                    <Label>Business</Label>
                    <select 
                      className="w-full border rounded-md p-2"
                      value={selectedBusiness}
                      onChange={(e) => setSelectedBusiness(e.target.value)}
                      data-testid="new-campaign-business"
                    >
                      <option value="">Select a business...</option>
                      {businesses.map((biz) => (
                        <option key={biz.id} value={biz.id}>{biz.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={createCampaign}
                    disabled={creating || !newName || !selectedBusiness}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {creating ? "Creating..." : "Create Campaign"}
                  </Button>
                  <Button variant="outline" onClick={() => setShowCreate(false)}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Campaign List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : campaigns.length > 0 ? (
            <div className="grid gap-4">
              {campaigns.map((campaign) => (
                <Card 
                  key={campaign.id} 
                  className="border-slate-200 hover:border-blue-200 transition-colors cursor-pointer"
                  onClick={() => navigate(`/campaigns/${campaign.id}`)}
                  data-testid={`campaign-${campaign.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-lg font-semibold text-slate-900">{campaign.name}</h3>
                          <Badge 
                            className={campaign.status === "active" 
                              ? "bg-green-100 text-green-700" 
                              : campaign.status === "paused" 
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-slate-100 text-slate-700"
                            }
                          >
                            {campaign.status}
                          </Badge>
                          {campaign.use_ai_content && (
                            <Badge variant="outline" className="border-purple-200 text-purple-600">
                              <Sparkles className="w-3 h-3 mr-1" />
                              AI
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          {campaign.stats?.total_customers || 0} customers · {campaign.stats?.sent || 0} sent · {campaign.stats?.reviewed || 0} reviews
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-600">
                            {campaign.stats?.review_rate || 0}%
                          </div>
                          <div className="text-xs text-slate-500">Review Rate</div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteCampaign(campaign.id);
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="p-12 text-center">
                <Mail className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No Campaigns Yet</h3>
                <p className="text-slate-500 mb-6">
                  Create your first campaign to start sending automated review requests to your customers.
                </p>
                <Button 
                  onClick={() => setShowCreate(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Campaign
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignsList;
